import { useEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';

interface Props {
  paymentId: string;
  pixCode: string;
  pixQrBase64: string;
  expiresAt: string;
  onSuccess: () => void;
  onClose: () => void;
}

export function PixCheckoutModal({ paymentId, pixCode, pixQrBase64, expiresAt, onSuccess, onClose }: Props) {
  const [copied, setCopied]   = useState(false);
  const [expired, setExpired] = useState(false);
  const [timeLeft, setTimeLeft] = useState('');
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const pollRef  = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    function tick() {
      const diff = new Date(expiresAt).getTime() - Date.now();
      if (diff <= 0) { setExpired(true); clearInterval(timerRef.current); return; }
      const m = Math.floor(diff / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${m}:${String(s).padStart(2, '0')}`);
    }
    tick();
    timerRef.current = setInterval(tick, 1000);
    return () => clearInterval(timerRef.current);
  }, [expiresAt]);

  useEffect(() => {
    pollRef.current = setInterval(async () => {
      const { data } = await supabase
        .from('pending_pix_payments')
        .select('status')
        .eq('mp_payment_id', paymentId)
        .maybeSingle();
      if (data?.status === 'completed') {
        clearInterval(pollRef.current);
        onSuccess();
      }
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [paymentId, onSuccess]);

  function copyCode() {
    navigator.clipboard.writeText(pixCode).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem',
    }}>
      <div style={{
        backgroundColor: 'var(--bg-surface)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-lg)',
        padding: '1.5rem',
        width: '100%', maxWidth: '420px',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem',
      }}>
        <div style={{ textAlign: 'center' }}>
          <span style={{ fontSize: '2rem' }}>🔑</span>
          <h2 style={{ fontFamily: 'var(--font-display)', color: 'var(--text-primary)', margin: '0.5rem 0 0.25rem' }}>
            Pague com PIX
          </h2>
          {!expired && timeLeft && (
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: 0 }}>
              Expira em <span style={{ color: 'var(--petrol)', fontWeight: 600 }}>{timeLeft}</span>
            </p>
          )}
          {expired && (
            <p style={{ fontSize: 'var(--text-caption)', color: '#fca5a5', margin: 0 }}>
              Código expirado. Feche e tente novamente.
            </p>
          )}
        </div>

        {!expired && (
          <>
            {pixQrBase64 && (
              <div style={{
                padding: '0.75rem',
                backgroundColor: '#fff',
                borderRadius: 'var(--radius-md)',
              }}>
                <img
                  src={`data:image/png;base64,${pixQrBase64}`}
                  alt="QR Code PIX"
                  style={{ width: '200px', height: '200px', display: 'block' }}
                />
              </div>
            )}

            <div style={{ width: '100%' }}>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', marginBottom: '0.5rem', textAlign: 'center' }}>
                Ou copie o código PIX:
              </p>
              <div style={{
                backgroundColor: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                padding: '0.6rem 0.75rem',
                fontFamily: 'monospace',
                fontSize: '11px',
                color: 'var(--text-secondary)',
                wordBreak: 'break-all',
                maxHeight: '72px',
                overflow: 'hidden',
                marginBottom: '0.5rem',
              }}>
                {pixCode}
              </div>
              <button
                className="btn btn-secondary"
                style={{ width: '100%' }}
                onClick={copyCode}
              >
                {copied ? '✓ Código copiado' : 'Copiar código PIX'}
              </button>
            </div>

            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', textAlign: 'center', margin: 0 }}>
              Após o pagamento, a confirmação é automática em até 1 minuto.
            </p>
          </>
        )}

        <button
          className="btn btn-secondary btn-sm"
          style={{ width: '100%' }}
          onClick={onClose}
        >
          {expired ? 'Fechar' : 'Cancelar'}
        </button>
      </div>
    </div>
  );
}
